/**
 * Contenu textuel du site vitrine Bar L'Alambic.
 * Tout le marketing copy se trouve ici — pas de hardcode dans les composants.
 */

export const BRAND = {
  name: "Bar L'Alambic",
  tagline: "Bar de dégustation",
  city: 'La Sarre, Abitibi-Témiscamingue',
  address: '77, 5e Avenue Est, La Sarre, QC J9Z 1L1',
  email: 'Bar.Alambic@outlook.com',
  phone: '(873) 476-0136',
  foundedYear: 2021,
};

export const SOCIALS = {
  facebook: 'https://www.facebook.com/barlalambic',
  instagram: 'https://www.instagram.com/explore/locations/110072124549996/bar-lalambic/',
};

export const HERO = {
  eyebrow: 'Depuis 2021 — La Sarre',
  titleStart: "L'univers",
  titleAccent: 'de la dégustation',
  subtitle:
    "Un bar de dégustation où chaque verre raconte une histoire. Whiskys rares, microbrasseries d'ici, vins d'importation privée et cocktails travaillés avec soin.",
  ctaPrimary: { label: 'Voir le menu', href: '/menu' },
  ctaSecondary: { label: 'Nous trouver', href: '#visite' },
};

export const BANDEAU = {
  text: 'Une nouvelle bouteille rejoint notre carte chaque mois',
};

export const STRIP_CHIFFRES: { value: string; label: string }[] = [
  { value: '60+', label: 'Whiskys' },
  { value: '8', label: 'Microbrasseries' },
  { value: '20', label: "Vins d'importation privée" },
  { value: '2021', label: 'Fondé' },
];

export const EXPERIENCE = {
  eyebrow: 'L\'expérience',
  title: 'Plus qu\'un bar — un lieu où se poser',
  description:
    "Pousser la porte du Bar L'Alambic, c'est entrer dans un univers feutré pensé pour les amateurs comme pour les curieux. On prend le temps de jaser, de conseiller, de partager une dégustation — sans prétention, avec passion.",
  values: [
    {
      title: 'Chaleureux',
      description:
        'Un éclairage tamisé, un accueil sincère et une ambiance qui invite à rester longtemps.',
    },
    {
      title: 'Partage',
      description:
        'Plateaux de dégustation, gros pichets pour la gang, bières d\'ici à découvrir ensemble.',
    },
    {
      title: 'Écoute',
      description:
        'On guide vos choix selon vos goûts. Un whisky tourbé pour la première fois ? On vous accompagne.',
    },
  ],
};

export const APERCUS = {
  eyebrow: 'Quelques signatures',
  title: 'Cocktails travaillés au demi-once près',
  items: [
    {
      name: 'Aperol Spritz',
      desc: "L'apéro italien — bulles, agrumes, amertume juste comme il faut.",
      image: '/images/marketing/aperol-spritz.webp',
    },
    {
      name: 'Whisky Sour',
      desc: 'Bourbon, sirop simple, citron, écume blanche. Un classique impeccable.',
      image: '/images/marketing/whisky-sour.webp',
    },
    {
      name: 'Alambic Tropical',
      desc: 'Notre signature : rhum, fruit de la passion, agrumes, garniture surette.',
      image: '/images/marketing/alambic-tropical.webp',
    },
  ],
};

export const CARTE = {
  eyebrow: 'Explore la carte',
  title: 'Quatre univers à découvrir',
  description:
    "Cocktails travaillés, vins d'importation privée, plateaux de dégustation et bouffe de quartier. La carte évolue chaque mois — demandez-nous ce qu'on a reçu cette semaine.",
  portals: [
    {
      kicker: '20+ créations',
      title: 'Nos cocktails',
      desc: "Travaillés par l'équipe, notés par les habitués.",
      cta: 'Découvrir la carte',
      href: '/cocktails',
    },
    {
      kicker: 'Importation privée',
      title: 'Nos vins',
      desc: "20 vins d'importation privée. Rouges, blancs, mousseux, bio & nature.",
      cta: 'Voir la cave',
      href: '/vins',
    },
    {
      kicker: '60+ whiskys, 8 microbrasseries',
      title: 'Dégustation',
      desc: 'Spiritueux, bières, plateaux — la sélection complète, prix et stock à jour.',
      cta: 'Voir la sélection',
      href: '/degustation',
    },
    {
      kicker: 'Bouffe + boissons',
      title: 'Notre menu',
      desc: 'Tartares, fromages, plateaux à partager, et toute la carte des boissons.',
      cta: 'Voir le menu',
      href: '/menu',
    },
  ],
};

export const VISITE = {
  eyebrow: 'Venir nous voir',
  title: 'Au cœur de La Sarre',
  address: BRAND.address,
  email: BRAND.email,
  phone: BRAND.phone,
  hours: [
    { day: 'Lundi — Mardi', hours: 'Fermé' },
    { day: 'Mercredi', hours: '17h00 — 00h00' },
    { day: 'Jeudi — Samedi', hours: '16h00 — 00h00' },
    { day: 'Dimanche', hours: 'Fermé' },
  ],
  note: 'Horaires sujets à modification. Contactez-nous pour les soirs spéciaux.',
};

export const PROMESSE = {
  eyebrow: 'Notre promesse',
  title: 'Vous repartez avec une histoire à raconter',
  description:
    "Que vous veniez pour un 5 à 7 entre collègues ou une soirée dégustation à deux, on s'assure que ce qui se passe ici reste un beau souvenir.",
  cta: { label: 'Réserver une table', href: '/reserver' },
};

/**
 * Intro éditoriale de la page /vins, affichée entre le hero et la liste.
 * Brise la transition "hero → mur de prix" qui rend la page sèche.
 */
export const CAVE_INTRO = {
  eyebrow: 'Importation privée',
  title: 'Vingt vins choisis',
  description:
    "Une cave d'importation privée renouvelée chaque saison. Nous sélectionnons des producteurs indépendants — du rouge généreux au mousseux festif, en passant par le blanc nature qui surprend. Demande-nous notre coup de cœur du moment.",
  stats: [
    { value: '20', label: 'Vins en cave' },
    { value: '4', label: 'Régions' },
    { value: '12', label: 'Cépages' },
  ],
  cta: { label: 'Voir la sélection', href: '#liste-vins' },
};

export const FOOTER = {
  tagline: 'Bar de dégustation — La Sarre, Abitibi',
  legal: `© ${new Date().getFullYear()} Bar L'Alambic. Tous droits réservés.`,
  links: [
    { label: 'Accueil', href: '/' },
    { label: 'Menu', href: '/menu' },
    { label: 'Cocktails', href: '/cocktails' },
    { label: 'Fûts', href: '/futs' },
    { label: 'Dégustation', href: '/degustation' },
    { label: 'Événements', href: '/evenements' },
    { label: 'À propos', href: '/a-propos' },
    { label: 'Emplois', href: '/emplois' },
    { label: 'Réserver', href: '/reserver' },
    { label: 'Nous écrire', href: 'mailto:Bar.Alambic@outlook.com' },
    { label: 'Politique de confidentialité', href: '/politique-confidentialite' },
  ],
};
